import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Bell, BellOff, Zap, CheckCircle, X } from "lucide-react";
import { BACKEND_URL } from "./networkConstants";

export function DealAlertWidget() {
  const [status, setStatus]     = useState(null);   // null | { subscribed, phone_masked }
  const [showForm, setShowForm] = useState(false);
  const [phone, setPhone]       = useState("");
  const [saving, setSaving]     = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/network/alerts/status`, { withCredentials: true })
      .then(r => setStatus(r.data))
      .catch(() => {});
  }, []);

  const subscribe = async () => {
    if (!phone.trim()) return;
    let formatted = phone.trim();
    // Auto-prepend +1 for US numbers without country code
    if (/^\d{10}$/.test(formatted)) formatted = `+1${formatted}`;
    if (!formatted.startsWith("+")) { toast.error("Include country code, e.g. +1 for US"); return; }
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/network/alerts/subscribe`, { phone: formatted }, { withCredentials: true });
      setStatus({ subscribed: true, phone_masked: `***-***-${formatted.slice(-4)}` });
      setShowForm(false);
      setPhone("");
      toast.success("Hot Deal Alerts activated! You'll get SMS for deals scoring 90+");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to subscribe");
    } finally { setSaving(false); }
  };

  const unsubscribe = async () => {
    try {
      await axios.delete(`${BACKEND_URL}/api/network/alerts/subscribe`, { withCredentials: true });
      setStatus({ subscribed: false, phone_masked: "" });
      toast.success("Unsubscribed from Hot Deal Alerts");
    } catch { toast.error("Failed to unsubscribe"); }
  };

  return (
    <div className={`rounded-2xl border p-4 transition-all ${
      status?.subscribed
        ? "bg-orange-500/[0.05] border-orange-500/25"
        : "bg-[#0D0D0D] border-white/[0.06]"
    }`} data-testid="deal-alert-widget">

      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`w-7 h-7 rounded-xl flex items-center justify-center flex-shrink-0 ${
            status?.subscribed ? "bg-orange-500/15 border border-orange-500/30" : "bg-[#D4AF37]/10 border border-[#D4AF37]/20"
          }`}>
            {status?.subscribed
              ? <Bell className="w-3.5 h-3.5 text-orange-400" />
              : <Zap  className="w-3.5 h-3.5 text-[#D4AF37]"  />}
          </div>
          <p className="text-[9px] text-white/30 uppercase tracking-widest font-bold">Hot Deal Alerts</p>
        </div>
        {status?.subscribed && (
          <span className="flex items-center gap-1 text-[9px] text-orange-400 bg-orange-500/10 border border-orange-500/20 px-2 py-0.5 rounded-full font-bold">
            <span className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-pulse" /> ON
          </span>
        )}
      </div>

      {status?.subscribed ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2 p-2.5 bg-orange-500/[0.06] border border-orange-500/15 rounded-xl">
            <CheckCircle className="w-3.5 h-3.5 text-orange-400 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-white/70 font-medium">SMS alerts active</p>
              {status.phone_masked && (
                <p className="text-[10px] text-white/30">{status.phone_masked}</p>
              )}
            </div>
          </div>
          <p className="text-[10px] text-white/30 leading-relaxed">
            You'll get instant SMS when a deal scores <span className="text-orange-400 font-bold">90+</span>.
          </p>
          <button onClick={unsubscribe} data-testid="unsubscribe-alerts-btn"
            className="w-full flex items-center justify-center gap-1.5 py-1.5 text-[10px] text-white/30 hover:text-red-400 border border-white/[0.06] hover:border-red-500/20 rounded-xl transition-all">
            <BellOff className="w-3 h-3" /> Turn off
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-[11px] text-white/40 leading-relaxed">
            Get instant SMS when a deal scores <span className="text-[#D4AF37] font-bold">90+/100</span> — before everyone else.
          </p>

          {!showForm ? (
            <button onClick={() => setShowForm(true)} data-testid="enable-alerts-btn"
              className="w-full py-2 flex items-center justify-center gap-1.5 bg-gradient-to-r from-orange-500/15 to-[#D4AF37]/10 border border-orange-500/25 text-orange-400 text-xs font-bold rounded-xl hover:from-orange-500/25 hover:border-orange-500/40 transition-all">
              <Bell className="w-3.5 h-3.5" /> Enable Deal Alerts
            </button>
          ) : (
            <div className="space-y-2">
              <div className="relative">
                <input value={phone} onChange={e => setPhone(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && subscribe()}
                  placeholder="+1 (555) 000-0000"
                  data-testid="alert-phone-input"
                  className="w-full bg-white/[0.04] border border-white/[0.1] rounded-xl px-3 py-2 text-xs text-white placeholder:text-white/20 focus:outline-none focus:border-orange-500/40" />
                <button onClick={() => setShowForm(false)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-white/20 hover:text-white/50">
                  <X className="w-3 h-3" />
                </button>
              </div>
              <button onClick={subscribe} disabled={saving || !phone.trim()} data-testid="confirm-alerts-btn"
                className="w-full py-2 flex items-center justify-center gap-1.5 bg-orange-500 hover:bg-orange-600 disabled:opacity-40 text-white text-xs font-bold rounded-xl transition-all">
                {saving ? "Saving…" : "Activate Alerts"}
              </button>
              <p className="text-[9px] text-white/20 text-center">E.164 format — 10-digit US numbers auto-formatted</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
